/**
 * Global Error Modal Component
 * Displays errors in a user-friendly modal with recovery options
 * 
 * Features:
 * - Material-UI styled modal with responsive design
 * - Professional messaging for client-facing applications
 * - Retry functionality for recoverable errors
 * - Support information (hidden technical details)
 * - Action buttons for user recovery options
 * - Integration with global error context
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  AlertTitle,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  NetworkWifi as NetworkIcon,
  Security as SecurityIcon,
  Code as CodeIcon,
  Refresh as RefreshIcon,
  Home as HomeIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  BugReport as BugReportIcon,
} from '@mui/icons-material';
import { withStyles } from '@material-ui/core/styles';
import { useError, ERROR_TYPES, ERROR_SEVERITY } from './ErrorContext';

const styles = (theme) => ({
  errorDialog: {
    '& .MuiDialog-paper': {
      minWidth: '400px',
      maxWidth: '700px',
      maxHeight: '90vh',
      [theme.breakpoints.down('sm')]: {
        minWidth: '90vw',
        margin: '16px',
        maxHeight: '95vh',
      },
    },
  },
  dialogTitle: {
    textAlign: 'center',
    paddingBottom: '8px',
  },
  errorHeader: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: '16px',
  },
  errorIcon: {
    fontSize: '48px',
    marginBottom: '12px',
  },
  criticalIcon: {
    color: '#d32f2f',
  },
  highIcon: {
    color: '#ca453e',
  },
  mediumIcon: {
    color: '#ff9800',
  },
  lowIcon: {
    color: '#2196f3',
  },
  errorTitle: {
    color: '#26516F',
    fontWeight: 'bold',
    textAlign: 'center',
  },
  errorContent: {
    marginBottom: '16px',
  },
  actionButtons: {
    display: 'flex',
    justifyContent: 'center',
    gap: '12px',
    flexWrap: 'wrap',
    padding: '16px 24px',
    '& .MuiButton-root': {
      minWidth: '100px',
    },
  },
  retryButton: {
    backgroundColor: '#26516F',
    color: 'white',
    '&:hover': {
      backgroundColor: '#1e3f5a',
    },
    '&:disabled': {
      backgroundColor: '#ccc',
    },
  },
  homeButton: {
    backgroundColor: '#ca453e',
    color: 'white',
    '&:hover': {
      backgroundColor: '#a83630',
    },
  },
  closeButton: {
    color: '#666',
    borderColor: '#666',
    '&:hover': {
      backgroundColor: '#f5f5f5',
      borderColor: '#333',
    },
  },
  technicalDetails: {
    marginTop: '16px',
    '& .MuiAccordionSummary-root': {
      backgroundColor: '#f8f9fa',
      borderRadius: '4px',
    },
  },
  detailsContent: {
    backgroundColor: '#f8f9fa',
    padding: '12px',
    borderRadius: '4px',
    fontFamily: 'monospace',
    fontSize: '12px',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    maxHeight: '150px',
    overflow: 'auto',
    marginBottom: '8px',
  },
  networkStatus: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '16px',
    padding: '8px 12px',
    backgroundColor: '#f5f5f5',
    borderRadius: '4px',
  },
  offlineStatus: {
    backgroundColor: '#ffebee',
    color: '#d32f2f',
  },
  onlineStatus: {
    backgroundColor: '#e8f5e8',
    color: '#2e7d32',
  },
});

const GlobalErrorModal = ({ classes }) => {
  // Modal disabled
  return null;

  const {
    currentError,
    isErrorModalOpen,
    networkStatus,
    retryCount,
    maxRetries,
    toggleErrorModal,
    retryOperation,
    removeError,
  } = useError();

  const [isRetrying, setIsRetrying] = useState(false);
  const [showTechnicalDetails, setShowTechnicalDetails] = useState(false);

  if (!currentError || !isErrorModalOpen) {
    return null;
  }

  // Get icon based on error type and severity
  const getErrorIcon = () => {
    const iconClass = 
      currentError.severity === ERROR_SEVERITY.CRITICAL ? classes.criticalIcon :
      currentError.severity === ERROR_SEVERITY.HIGH ? classes.highIcon :
      currentError.severity === ERROR_SEVERITY.MEDIUM ? classes.mediumIcon :
      classes.lowIcon;

    switch (currentError.type) {
      case ERROR_TYPES.NETWORK:
        return <NetworkIcon className={`${classes.errorIcon} ${iconClass}`} />;
      case ERROR_TYPES.AUTHENTICATION:
      case ERROR_TYPES.AUTHORIZATION:
        return <SecurityIcon className={`${classes.errorIcon} ${iconClass}`} />;
      case ERROR_TYPES.APPLICATION:
        return <CodeIcon className={`${classes.errorIcon} ${iconClass}`} />;
      case ERROR_TYPES.VALIDATION:
        return <WarningIcon className={`${classes.errorIcon} ${iconClass}`} />;
      default:
        return currentError.severity === ERROR_SEVERITY.CRITICAL || currentError.severity === ERROR_SEVERITY.HIGH
          ? <ErrorIcon className={`${classes.errorIcon} ${iconClass}`} />
          : <InfoIcon className={`${classes.errorIcon} ${iconClass}`} />;
    }
  };

  // Get alert severity
  const getAlertSeverity = () => {
    switch (currentError.severity) {
      case ERROR_SEVERITY.CRITICAL:
      case ERROR_SEVERITY.HIGH:
        return 'error';
      case ERROR_SEVERITY.MEDIUM:
        return 'warning';
      default:
        return 'info';
    }
  };

  // Handle retry
  const handleRetry = async () => {
    if (!currentError.retryable || isRetrying) return;

    setIsRetrying(true);
    try {
      const success = await retryOperation(currentError.id);
      if (success) {
        handleClose();
      }
    } finally {
      setIsRetrying(false);
    }
  };

  // Handle close
  const handleClose = () => {
    toggleErrorModal(false);
    if (currentError) {
      removeError(currentError.id);
    }
  };

  // Handle go home
  const handleGoHome = () => {
    handleClose();
    window.location.href = '/kabahagi';
  };

  // Handle refresh
  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <Dialog
      open={isErrorModalOpen}
      onClose={handleClose}
      className={classes.errorDialog}
      disableEscapeKeyDown={false}
    >
      <DialogTitle className={classes.dialogTitle}>
        <Box className={classes.errorHeader}>
          {getErrorIcon()}
          <Typography variant="h5" className={classes.errorTitle}>
            {currentError.type === ERROR_TYPES.NETWORK ? 'Connection Issue' :
             currentError.type === ERROR_TYPES.APPLICATION ? 'System Notice' :
             currentError.type === ERROR_TYPES.AUTHENTICATION ? 'Access Issue' :
             currentError.type === ERROR_TYPES.AUTHORIZATION ? 'Permission Issue' :
             currentError.type === ERROR_TYPES.VALIDATION ? 'Input Notice' :
             currentError.type === ERROR_TYPES.SERVER ? 'Service Notice' :
             'System Notice'}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Network status indicator */}
        {currentError.type === ERROR_TYPES.NETWORK && (
          <Box className={`${classes.networkStatus} ${networkStatus === 'offline' ? classes.offlineStatus : classes.onlineStatus}`}>
            <NetworkIcon />
            <Typography variant="body2">
              Connection Status: {networkStatus === 'offline' ? 'Offline' : 'Online'}
            </Typography>
          </Box>
        )}

        {/* Main error alert */}
        <Alert severity={getAlertSeverity()} className={classes.errorContent}>
          <AlertTitle>
            Heads Up
          </AlertTitle>
          <Typography variant="body1">
            Something unexpected happened. We're working to resolve this. Please try again in a moment.
          </Typography>
        </Alert>

        {/* Retry information */}
        {currentError.retryable && retryCount > 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              Attempt {retryCount} of {maxRetries}
            </Typography>
          </Alert>
        )}

        {/* Technical details - Hidden by default for professional appearance */}
        {process.env.NODE_ENV === 'development' && (
          <Accordion
            className={classes.technicalDetails}
            expanded={showTechnicalDetails}
            onChange={(_, isExpanded) => setShowTechnicalDetails(isExpanded)}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box display="flex" alignItems="center" gap={1}>
                <BugReportIcon fontSize="small" />
                <Typography variant="subtitle2" color="textSecondary">
                  Support Information
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  If you need to contact support, please provide this reference:
                </Typography>
                <Box className={classes.detailsContent}>
                  Reference ID: {currentError.id}
                </Box>
                <Typography variant="body2" color="textSecondary" gutterBottom sx={{ mt: 2 }}>
                  Time: {new Date(currentError.timestamp).toLocaleString()}
                </Typography>
              </Box>
            </AccordionDetails>
          </Accordion>
        )}
      </DialogContent>

      <Divider />

      <DialogActions className={classes.actionButtons}>
        {/* Retry button for retryable errors */}
        {currentError.retryable && retryCount < maxRetries && (
          <Button
            onClick={handleRetry}
            variant="contained"
            className={classes.retryButton}
            startIcon={isRetrying ? <CircularProgress size={16} /> : <RefreshIcon />}
            disabled={isRetrying || networkStatus === 'offline'}
          >
            {isRetrying ? 'Trying Again...' : 'Try Again'}
          </Button>
        )}

        {/* Refresh button for application errors */}
        {currentError.type === ERROR_TYPES.APPLICATION && (
          <Button
            onClick={handleRefresh}
            variant="contained"
            className={classes.retryButton}
            startIcon={<RefreshIcon />}
          >
            Refresh Page
          </Button>
        )}

        {/* Close button - Always available */}
        <Button
          onClick={handleClose}
          variant="outlined"
          className={classes.closeButton}
          startIcon={<CloseIcon />}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

GlobalErrorModal.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(GlobalErrorModal);